import PermissionButton from 'components/buttons/PermissionButton';
import EmptyState from 'components/EmptyState';
import { ChevronLeft, ChevronRight, Delete as DeleteIcon, Edit as EditIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { DataTable, useDataTableSortString } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Pagination, PaginationContent, PaginationItem } from 'components/ui/pagination';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useAddTimecardTemplateMutation,
    useDeleteTimecardTemplateMutation,
    useGetTimecardTemplatesQuery,
    useUpdateTimecardTemplateMutation,
} from 'store/api/timecard-templates';
import { TimecardTemplateDto, TimecardTemplateSaveDto } from 'types/timecards';
import { errorAlert, successAlert } from 'utils/alert';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { pageOfTimecardTemplates, TimecardTemplateRow } from './timecardTemplateRows';
import TimecardTemplateEditModal from './TimecardTemplateEditModal';

export function TimecardTemplatesControl(): JSX.Element {
    const { openModal, closeModal } = useModal();
    const { classifiers } = useClassifiers({ codes: ['LANGUAGE', 'MATTER_TYPE'] });
    const { data: templates, isLoading } = useGetTimecardTemplatesQuery();
    const canEdit = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const [addTemplate] = useAddTimecardTemplateMutation();
    const [updateTemplate] = useUpdateTimecardTemplateMutation();
    const [deleteTemplate] = useDeleteTimecardTemplateMutation();
    const [filter, setFilter] = useState('');
    const [sort, setSort] = useState('');
    const [page, setPage] = useState(1);

    // Resolving both classifier labels once per row keeps `getClassifierText` — a linear scan of the
    // classifier's values — out of the filter predicate and the comparator.
    const rows: TimecardTemplateRow[] = useMemo(
        () =>
            (templates ?? []).map((template) => ({
                ...template,
                caseType: getClassifierText(classifiers['MATTER_TYPE'], template.matterTypeCode, undefined, 'eng'),
                language: getClassifierText(classifiers['LANGUAGE'], template.langCode),
            })),
        [templates, classifiers]
    );

    const submit = useCallback(
        async (dto: TimecardTemplateSaveDto, id?: number): Promise<void> => {
            try {
                await (id ? updateTemplate({ id, ...dto }) : addTemplate(dto)).unwrap();
                successAlert(t('configuration.timecard-template-update-success'));
                closeModal();
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [addTemplate, updateTemplate, closeModal]
    );

    const openEditModal = useCallback(
        (template?: TimecardTemplateDto): void => {
            openModal(
                <TimecardTemplateEditModal
                    initialData={template}
                    onSubmit={(dto) => {
                        void submit(dto, template?.id);
                    }}
                    onClose={closeModal}
                />,
                closeModal
            );
        },
        [submit, openModal, closeModal]
    );

    const openDeleteModal = useCallback(
        (template: TimecardTemplateDto): void => {
            openModal(
                <ConfirmationModal
                    content={t('configuration.timecard-template-delete-confirmation')}
                    onCancel={closeModal}
                    onConfirm={() => {
                        void (async () => {
                            try {
                                await deleteTemplate(template.id).unwrap();
                                successAlert(t('configuration.timecard-template-delete-success'));
                            } catch (e) {
                                console.error('delete error: ', e);
                                errorAlert(t('forms.error-submitting-the-form'));
                            }
                            closeModal();
                        })();
                    }}
                />,
                closeModal
            );
        },
        [deleteTemplate, openModal, closeModal]
    );

    const columns = useMemo<DataTableColumnDef<TimecardTemplateRow>[]>(
        () => [
            {
                header: t('matters.case-type'),
                id: 'caseType',
                accessorKey: 'caseType',
                meta: { widthWeight: 1 },
            },
            {
                header: t('configuration.timecard-template-language'),
                id: 'language',
                accessorKey: 'language',
                meta: { widthWeight: 1 },
            },
            {
                header: t('configuration.timecard-template-text'),
                id: 'templateText',
                accessorKey: 'templateText',
                meta: { widthWeight: 3, absorbSlack: true },
                // Stored line breaks are part of the template; `pre-wrap` is what shows them.
                cell: ({ row }) => <span className="whitespace-pre-wrap">{row.original.templateText}</span>,
            },
            {
                header: t('valid-from'),
                id: 'validFrom',
                accessorKey: 'validFrom',
                meta: { widthWeight: 1 },
                cell: ({ row }) => <FormattedDate value={row.original.validFrom} />,
            },
            {
                header: t('valid-to'),
                id: 'validUntil',
                accessorKey: 'validUntil',
                meta: { widthWeight: 1 },
                cell: ({ row }) => <FormattedDate value={row.original.validUntil} />,
            },
            {
                header: '',
                id: 'actions',
                enableSorting: false,
                meta: { fixedPx: 96, textAlign: 'right' },
                cell: ({ row }) =>
                    canEdit ? (
                        <>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={t('edit')}
                                onClick={() => openEditModal(row.original)}
                            >
                                <EditIcon />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive"
                                aria-label={t('delete')}
                                onClick={() => openDeleteModal(row.original)}
                            >
                                <DeleteIcon />
                            </Button>
                        </>
                    ) : null,
            },
        ],
        [canEdit, openEditModal, openDeleteModal]
    );

    // A page index only means anything against the set it indexed: narrowing the filter or
    // reordering the rows drops the reader somewhere arbitrary in a list they never scrolled to.
    // These two are the only state changes that reshuffle the set, so both restart at page one.
    const applyFilter = useCallback((next: string): void => {
        setFilter(next);
        setPage(1);
    }, []);

    const applySort = useCallback((next: string): void => {
        setSort(next);
        setPage(1);
    }, []);

    const { sorting, onSortingChange } = useDataTableSortString(sort, applySort, columns);

    const {
        rows: pageRows,
        page: currentPage,
        pageCount,
    } = useMemo(() => pageOfTimecardTemplates(rows, filter, sort, page), [rows, filter, sort, page]);

    const filterLabel = t('configuration.timecard-templates-filter');

    return (
        <>
            <h3 className="text-2xl leading-8 font-semibold text-balance">
                <FormattedMessage id="configuration.timecard-templates" />
            </h3>
            <div className="flex flex-col gap-3">
                <Input
                    type="search"
                    value={filter}
                    onChange={(event) => applyFilter(event.target.value)}
                    placeholder={filterLabel}
                    aria-label={filterLabel}
                    className="h-8 max-w-xs"
                />
                <DataTable<TimecardTemplateRow>
                    columns={columns}
                    data={pageRows}
                    getRowId={(row) => String(row.id)}
                    columnConfigId="configuration-timecard-templates"
                    // Template text wraps to several lines, and the engine centres cell content. On
                    // a tall row that floats the dates and the row actions into the middle of it,
                    // away from the line they belong to.
                    className="[&_tbody_td]:items-start!"
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    // Skeleton rows while the first fetch is out; without it the tenant-empty state
                    // flashes over every load, since an absent response and an empty one look alike.
                    isLoading={isLoading}
                    // `filtered` is the state a reader can undo, so it offers the clear-filter action
                    // and its own copy instead of the tenant-empty line.
                    emptyState={
                        <EmptyState
                            size="inline"
                            variant={rows.length ? 'filtered' : 'empty'}
                            titleId="configuration.timecard-templates-empty"
                            onClearFilters={() => applyFilter('')}
                            surface="configuration-timecard-templates"
                        />
                    }
                />
                <div className="flex items-center justify-between gap-4">
                    <PermissionButton
                        permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                        variant="outlined"
                        size="small"
                        className="h-8"
                        onClick={() => openEditModal()}
                    >
                        <FormattedMessage id="configuration.timecard-template-add" />
                    </PermissionButton>
                    {pageCount > 1 ? (
                        <Pagination className="mx-0 w-auto" aria-label={t('pagination.pagination')}>
                            <PaginationContent>
                                <PaginationItem>
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        aria-label={t('pagination.previous-page')}
                                        disabled={currentPage <= 1}
                                        onClick={() => setPage(currentPage - 1)}
                                    >
                                        <ChevronLeft />
                                    </Button>
                                </PaginationItem>
                                {/* The rows swap without the focused button's name changing, so this
                                    line is the only thing that says a page turned. `atomic` because
                                    the message interpolates: without it only the changed number is
                                    read out, which on its own says nothing. */}
                                <PaginationItem
                                    className="text-fg-secondary px-1 text-xs"
                                    aria-live="polite"
                                    aria-atomic
                                >
                                    <FormattedMessage
                                        id="pagination.page-of"
                                        values={{ page: currentPage, pageCount }}
                                    />
                                </PaginationItem>
                                <PaginationItem>
                                    <Button
                                        variant="ghost"
                                        size="icon-sm"
                                        aria-label={t('pagination.next-page')}
                                        disabled={currentPage >= pageCount}
                                        onClick={() => setPage(currentPage + 1)}
                                    >
                                        <ChevronRight />
                                    </Button>
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    ) : null}
                </div>
            </div>
        </>
    );
}
